import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import React from "react";
import { gsap } from "gsap";
export const AUTOPLAY_DURATION = 10_000;
export const Section = _styled.section.withConfig({
  componentId: "sc-sf-gi0lbo-0"
})`
  ${{
  "overflow": "hidden"
}}

  .splide__track {
    overflow: visible !important;
  }

  @keyframes cardFill {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }

  @keyframes cardMediaZoom {
    from {
      transform: scale(1.06);
    }
    to {
      transform: scale(1);
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function animateCardIn(card: HTMLDivElement, SplitTextClass?: any): {
  tl: gsap.core.Timeline;
  splits: any[];
} {
  const allElements = card.querySelectorAll("[data-animate]");
  gsap.set(allElements, {
    clearProps: "all"
  });
  const logo = card.querySelector('[data-animate="logo"]');
  const title = card.querySelector('[data-animate="title"]');
  const description = card.querySelector('[data-animate="description"]');
  const cta = card.querySelector('[data-animate="cta"]');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const splits: any[] = [];
  const tl = gsap.timeline({
    defaults: {
      ease: "cubic-bezier(0.125, 0.425, 0.27, 1)"
    }
  });
  if (logo) {
    tl.fromTo(logo, {
      opacity: 0,
      y: 16
    }, {
      opacity: 1,
      y: 0,
      duration: 0.5
    }, 0);
  }
  if (title) {
    const heading = title.querySelector("h1, h2, h3, h4, h5, h6") || title.firstElementChild;
    if (SplitTextClass && heading) {
      const split = new SplitTextClass(heading, {
        type: "lines"
      });
      splits.push(split);
      const inners: HTMLElement[] = [];
      split.lines.forEach((line: HTMLElement) => {
        line.style.overflow = "hidden";
        const inner = document.createElement("div");
        while (line.firstChild) {
          inner.appendChild(line.firstChild);
        }
        line.appendChild(inner);
        inners.push(inner);
      });
      tl.fromTo(inners, {
        yPercent: 100
      }, {
        yPercent: 0,
        duration: 0.6,
        stagger: 0.08
      }, 0.15);
    } else {
      tl.fromTo(title, {
        opacity: 0
      }, {
        opacity: 1,
        duration: 0.5
      }, 0.15);
    }
  }
  if (description) {
    tl.fromTo(description, {
      opacity: 0,
      y: 16
    }, {
      opacity: 1,
      y: 0,
      duration: 0.5
    }, 0.25);
  }
  if (cta) {
    tl.fromTo(cta, {
      opacity: 0,
      y: 16
    }, {
      opacity: 1,
      y: 0,
      duration: 0.5
    }, 0.35);
  }
  return {
    tl,
    splits
  };
}
export function animateCardOut(card: HTMLDivElement): gsap.core.Timeline {
  const elements = card.querySelectorAll("[data-animate]");
  return gsap.timeline().to(elements, {
    y: -20,
    opacity: 0,
    duration: 0.3,
    stagger: 0.04,
    ease: "power2.in"
  });
}
export function CardProgressBar({
  onComplete
}: {
  onComplete: () => void;
}) {
  return <_StyledDiv style={{
    backgroundColor: "rgba(0, 82, 255, 0.24)"
  }} $_css={[{
    "position": "absolute",
    "left": "0",
    "right": "0",
    "top": "0",
    "zIndex": "20",
    "height": "0.4rem",
    "width": "100%"
  }]}>
      <_StyledDiv2 style={{
      animation: `cardFill ${AUTOPLAY_DURATION}ms linear forwards`
    }} onAnimationEnd={onComplete} $_css2={[{
      "height": "100%",
      "transformOrigin": "left",
      "--tw-bg-opacity": "1",
      "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))"
    }]} />
    </_StyledDiv>;
}
export function CardProgressOverlay({
  onComplete
}: {
  onComplete: () => void;
}) {
  return <_StyledDiv3 style={{
    backgroundColor: "rgba(0, 82, 255, 0.4)",
    animation: `cardFill ${AUTOPLAY_DURATION}ms linear forwards`
  }} onAnimationEnd={onComplete} $_css3={[{
    "position": "absolute",
    "inset": "0",
    "zIndex": "20",
    "transformOrigin": "left"
  }]} />;
}
type SvgLogoProps = {
  src: string;
  alt: string;
  position?: string;
  tone?: "dark" | "light";
} & Omit<React.ComponentPropsWithRef<"img">, "alt" | "src">;
export function SvgLogo({
  src,
  alt,
  position = "center",
  style,
  tone = "dark",
  ...rest
}: SvgLogoProps) {
  return <_StyledImg src={src} alt={alt} {...rest} style={{
    filter: tone === "light" ? "brightness(0) invert(1)" : "brightness(0)",
    objectPosition: position,
    ...style
  }} $_css4={[{
    "display": "block",
    "width": "100%",
    "maxWidth": "12rem",
    "objectFit": "contain"
  }]} />;
}
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type PartnerGalleryViewLink = {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
export type PartnerGalleryViewItem = {
  _key?: string;
  description?: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  link?: any;
  logo?: {
    alt?: string | null;
    url?: string | null;
  } | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  media?: any;
  title?: string | null;
};
export type PartnerGalleryLayoutProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: ReadonlyArray<any>;
  mode: string;
  hasTopDivider: boolean;
  isHighlighted?: boolean;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  showGalleryNavigation?: boolean;
  theme?: string;
};
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-gi0lbo-1"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-gi0lbo-2"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-gi0lbo-3"
})`${p => p.$_css3}`;
var _StyledImg = /*#__PURE__*/_styled5("img").withConfig({
  displayName: "shared___StyledImg",
  componentId: "sc-17ry3ok-0"
})(["", ""], p => p.$_css4);